/**
 * The template viewer — FR-I16, T-4.56, interface I-7.
 *
 * `/site/templates/:code/preview?profession=<p>`, opened by the gallery's
 * *Preview* in a **new tab**. The owner, 2026-09-25: *"it should be in new tab
 * with mobile, and desktop view"*. A full-screen staff route **without the app
 * chrome** (router.tsx mounts it outside `App`, inside the session guard): a
 * slim toolbar and, below it, the demo in the frame the firm chose.
 *
 * **The frame is a real viewport, then scaled.** Desktop, Tablet and Mobile
 * lay the demo out at 1280, 820 and 390 CSS pixels — so the site's own media
 * queries decide what each device sees, which is the whole question a firm is
 * asking when it presses Mobile — and only then scale the frame down to fit
 * the window. A CSS-narrowed desktop page would be answering a different
 * question.
 *
 * **The page menu is read from the demo itself.** The service's sample files
 * (`GET /site/samples/{p}`) and the SSR demo's `builtInSample` are two
 * different sample sets with different page lists (the demo has *Team*; the
 * service's CA sample does not). A menu built from the service would offer a
 * page the frame then 404s on. So the viewer fetches the demo's home page —
 * same origin, no session, no CORS — and lists the pages its own navigation
 * links to.
 *
 * *Use this template* is the gallery's own call (`PUT /site` with
 * `{template}`), then back to the gallery **in this tab** with a confirmation.
 */

import { Alert, Badge, Button, DropdownMenu, EmptyState, buttonClass } from '@fiveox/ui'
import {
  Check,
  ChevronDown,
  FileText,
  Loader2,
  Monitor,
  Smartphone,
  Tablet,
  X,
} from 'lucide-react'
import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react'
import type { ReactNode } from 'react'
import { Link, useNavigate, useParams, useSearchParams } from 'react-router'

import { SITE, useEvaluation } from '../../lib/access'
import { ApiError } from '../../lib/api'
import { demoHref, site, type Template } from '../../lib/api/site'
import { professionFit } from './Templates'

// -- the three frames ---------------------------------------------------------

export type Device = 'desktop' | 'tablet' | 'mobile'

/** FR-I16's widths, in CSS pixels, and the height each device is drawn at
 *  when the window leaves room for it. Desktop and tablet take the window's
 *  height instead; a phone keeps its own shape. */
export const DEVICES: Record<Device, { label: string; width: number; height: number; icon: ReactNode }> = {
  desktop: { label: 'Desktop', width: 1280, height: 800, icon: <Monitor size={16} /> },
  tablet: { label: 'Tablet', width: 820, height: 1180, icon: <Tablet size={16} /> },
  mobile: { label: 'Mobile', width: 390, height: 844, icon: <Smartphone size={16} /> },
}

const DEVICE_ORDER: Device[] = ['desktop', 'tablet', 'mobile']

/** The phone's outline, each side, in unscaled pixels. */
const BEZEL = 12
/** The desktop frame's window bar, in unscaled pixels. */
const WINDOW_BAR = 36
/** Room left around the frame inside the stage. */
const STAGE_PAD = 24

/**
 * How a device's frame fits a stage of `stageW` × `stageH`.
 *
 * Never scaled **up** — a 390px phone on a 1440px monitor is drawn at 390px.
 * Desktop and tablet fill the stage's height (the frame is a window, and a
 * window is as tall as the screen allows); the phone keeps its 390 × 844 shape
 * and shrinks as a whole when the window is short.
 *
 * Exported for the tests, because "scaled to fit" is arithmetic and the DOM in
 * a test has no size to measure.
 */
export function fitFrame(
  device: Device,
  stageW: number,
  stageH: number,
): { scale: number; outerW: number; outerH: number; viewportH: number } {
  const spec = DEVICES[device]
  const availW = Math.max(stageW - STAGE_PAD * 2, 1)
  const availH = Math.max(stageH - STAGE_PAD * 2, 1)
  if (device === 'mobile') {
    const outerW = spec.width + BEZEL * 2
    const outerH = spec.height + BEZEL * 2
    const scale = Math.min(1, availW / outerW, availH / outerH)
    return { scale, outerW, outerH, viewportH: spec.height }
  }
  const chrome = device === 'desktop' ? WINDOW_BAR : 0
  const outerW = spec.width + 2 // the 1px border either side
  const scale = Math.min(1, availW / outerW)
  // Fill the stage's height after scaling, but never shorter than a screenful
  // of the device would be at this width.
  const outerH = Math.max(availH / scale, Math.min(spec.height, 480))
  return { scale, outerW, outerH, viewportH: outerH - chrome - 2 }
}

// -- the demo's pages -----------------------------------------------------------

export interface DemoPage {
  /** `''` is the home page. */
  slug: string
  title: string
}

/**
 * The pages a demo's own navigation links to, in its order, home first.
 *
 * Reads `nav a` first — the header menu — and falls back to every anchor into
 * the demo when a theme draws its menu some other way. Deduplicated by slug,
 * so the header and the footer naming the same page make one entry.
 */
export function demoPages(html: string, code: string): DemoPage[] {
  const base = `/_demo/${code}`
  const doc = new DOMParser().parseFromString(html, 'text/html')
  const collect = (selector: string): DemoPage[] => {
    const seen = new Map<string, DemoPage>()
    for (const a of Array.from(doc.querySelectorAll<HTMLAnchorElement>(selector))) {
      const href = a.getAttribute('href') ?? ''
      const path = href.split(/[?#]/)[0] ?? ''
      if (path !== base && !path.startsWith(`${base}/`)) continue
      const slug = path.slice(base.length).replace(/^\/+|\/+$/g, '')
      if (seen.has(slug)) continue
      const title = (a.textContent ?? '').replace(/\s+/g, ' ').trim()
      seen.set(slug, { slug, title: title || slug || 'Home' })
    }
    return [...seen.values()]
  }
  let pages = collect('nav a[href]')
  if (pages.length === 0) pages = collect('a[href]:not(.brand)')
  const home = pages.find((p) => p.slug === '')
  const rest = pages.filter((p) => p.slug !== '')
  return [home ?? { slug: '', title: 'Home' }, ...rest]
}

/** One page of the demo, keeping the firm's profession on it. */
export function demoPageHref(code: string, profession: string | null, slug: string): string {
  const base = demoHref(code, profession)
  if (!slug) return base
  const [path, query] = base.split('?')
  return `${path}/${slug.split('/').map(encodeURIComponent).join('/')}${query ? `?${query}` : ''}`
}

// -- the screen ---------------------------------------------------------------

export function TemplateViewer() {
  const { code = '' } = useParams()
  const [params] = useSearchParams()
  const navigate = useNavigate()
  const { can, loading: accessLoading } = useEvaluation()
  const editable = can(SITE, 'can_update')

  const [template, setTemplate] = useState<Template | null>(null)
  const [missing, setMissing] = useState(false)
  const [profession, setProfession] = useState<string | null>(params.get('profession'))
  const [current, setCurrent] = useState<string | null>(null)
  const [pages, setPages] = useState<DemoPage[]>([{ slug: '', title: 'Home' }])
  const [slug, setSlug] = useState('')
  const [device, setDevice] = useState<Device>('desktop')
  const [frameLoading, setFrameLoading] = useState(true)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  // The template and the firm's profession. `?profession=` wins, because the
  // gallery that opened this tab already knew it; without one, the firm's own.
  useEffect(() => {
    let cancelled = false
    site
      .templates()
      .then((body) => {
        if (cancelled) return
        const found = body.data.templates.find((t) => t.code === code) ?? null
        setTemplate(found)
        setMissing(found === null)
        if (!params.get('profession')) setProfession(body.data.profession)
      })
      .catch((err: unknown) => {
        if (cancelled) return
        setError(err instanceof ApiError ? err.message : 'This template could not be loaded.')
      })
    // Which template the firm is on, for *In use*. Costs a label if it fails.
    site
      .read()
      .then((body) => {
        if (!cancelled) setCurrent(body.data.site?.template ?? null)
      })
      .catch(() => undefined)
    return () => {
      cancelled = true
    }
    // `params` is read once, deliberately: this tab is opened for one template.
  }, [code])

  // The demo's own page list (see the header on why it is read from the demo).
  useEffect(() => {
    if (!code || typeof fetch === 'undefined') return
    let cancelled = false
    fetch(demoHref(code, profession))
      .then((res) => (res.ok ? res.text() : ''))
      .then((html) => {
        if (!cancelled && html) setPages(demoPages(html, code))
      })
      .catch(() => undefined)
    return () => {
      cancelled = true
    }
  }, [code, profession])

  useEffect(() => {
    if (template) document.title = `${template.name} — template preview · ProDesk`
  }, [template])

  const src = demoPageHref(code, profession, slug)
  useEffect(() => setFrameLoading(true), [src])

  const close = useCallback(() => {
    // A tab this app opened with `target="_blank"` has one history entry and
    // may close itself. One that cannot (reached by a typed address, or with
    // history behind it) goes back to the gallery instead of doing nothing.
    window.close()
    window.setTimeout(() => navigate('/site/templates'), 150)
  }, [navigate])

  const use = async () => {
    if (!template) return
    setBusy(true)
    setError(null)
    try {
      await site.update({ template: template.code })
      navigate('/site/templates', { state: { applied: template.code } })
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'That template could not be applied.')
      setBusy(false)
    }
  }

  const isCurrent = template !== null && template.code === current
  const page = pages.find((p) => p.slug === slug) ?? pages[0]

  if (missing) {
    return (
      <div className="flex min-h-dvh items-center justify-center bg-muted p-4">
        <EmptyState
          title="There is no template by that name"
          description="The link may be from an older version of the gallery. Every template there is can be previewed from the gallery."
          action={
            <Link to="/site/templates" className={buttonClass({ variant: 'primary', size: 'sm' })}>
              Go to the templates
            </Link>
          }
        />
      </div>
    )
  }

  return (
    <div className="flex h-dvh flex-col bg-muted" data-template-viewer={code}>
      <header className="flex flex-wrap items-center gap-x-3 gap-y-2 border-b border-border bg-background px-3 py-2 sm:px-4">
        {/* Left: close and the name. */}
        <div className="flex min-w-0 flex-1 items-center gap-2">
          <button
            type="button"
            onClick={close}
            aria-label="Close the preview"
            title="Close the preview"
            className={buttonClass({ variant: 'ghost', size: 'sm', className: 'w-9 shrink-0 px-0' })}
          >
            <X size={18} />
          </button>
          <span aria-hidden className="h-6 w-px shrink-0 bg-border" />
          <div className="min-w-0">
            <h1 className="flex items-center gap-2 truncate text-sm font-semibold text-foreground">
              <span className="truncate">{template?.name ?? 'Template preview'}</span>
              {isCurrent && (
                <Badge tone="success" size="sm">
                  <Check size={11} /> In use
                </Badge>
              )}
            </h1>
            <p className="hidden truncate text-xs text-muted-foreground sm:block">
              {template ? `${professionFit(template.profession)} · ${template.trait}` : 'Loading…'}
            </p>
          </div>
        </div>

        {/* Centre: the three frames. */}
        <div
          role="group"
          aria-label="Device"
          className="order-last flex w-full items-center justify-center gap-1 sm:order-none sm:w-auto"
        >
          <div className="inline-flex rounded-lg bg-muted p-1">
            {DEVICE_ORDER.map((d) => (
              <button
                key={d}
                type="button"
                aria-pressed={device === d}
                onClick={() => setDevice(d)}
                title={`${DEVICES[d].label} — ${DEVICES[d].width}px wide`}
                className={[
                  'inline-flex h-8 items-center gap-1.5 rounded-md px-2 text-sm font-medium transition-colors sm:px-3',
                  device === d
                    ? 'bg-background text-foreground shadow-sm'
                    : 'text-muted-foreground hover:text-foreground',
                ].join(' ')}
              >
                {DEVICES[d].icon}
                <span>{DEVICES[d].label}</span>
              </button>
            ))}
          </div>
          <DropdownMenu
            label="Pages of the demo"
            heading="Pages"
            align="end"
            items={pages.map((p) => ({
              id: p.slug || 'home',
              label: p.title,
              icon: p.slug === slug ? <Check size={14} /> : <FileText size={14} />,
              onSelect: () => setSlug(p.slug),
            }))}
            trigger={({ open: _open, ...props }) => (
              <button
                type="button"
                {...props}
                aria-label={`Page: ${page?.title ?? 'Home'}`}
                className={buttonClass({ variant: 'ghost', size: 'sm', className: 'max-w-40 px-2 sm:px-3' })}
              >
                <FileText size={16} />
                <span className="hidden truncate sm:inline">{page?.title ?? 'Home'}</span>
                <ChevronDown size={14} />
              </button>
            )}
          />
        </div>

        {/* Right: the one primary action. */}
        <div className="flex shrink-0 items-center gap-2 sm:flex-1 sm:justify-end">
          {template && !accessLoading && editable && !isCurrent && (
            <Button size="sm" loading={busy} loadingText="Applying…" onClick={() => void use()}>
              Use this template
            </Button>
          )}
          {template && isCurrent && (
            <Link
              to="/site/templates"
              className={buttonClass({ variant: 'outline', size: 'sm' })}
            >
              Back to the gallery
            </Link>
          )}
        </div>
      </header>

      {error && (
        <div className="border-b border-border bg-background px-4 py-2">
          <Alert tone="error">{error}</Alert>
        </div>
      )}

      <Stage device={device}>
        {(fit) => (
          <DeviceFrame device={device} fit={fit}>
            <div className="relative h-full w-full">
              <iframe
                src={src}
                title={`${template?.name ?? code} — ${page?.title ?? 'Home'}, ${DEVICES[device].label.toLowerCase()} preview`}
                sandbox=""
                data-device={device}
                onLoad={() => setFrameLoading(false)}
                className="block border-0 bg-white"
                style={{ width: DEVICES[device].width, height: fit.viewportH }}
              />
              {frameLoading && (
                <div className="absolute inset-0 flex items-center justify-center bg-white/70">
                  <Loader2 size={28} className="animate-spin text-muted-foreground" />
                </div>
              )}
            </div>
          </DeviceFrame>
        )}
      </Stage>

      <p className="border-t border-border bg-background px-4 py-1.5 text-center text-xs text-muted-foreground">
        Sample content for a practice that does not exist. Your own pages keep their words when you
        switch.
      </p>
    </div>
  )
}

// -- layout -------------------------------------------------------------------

type Fit = ReturnType<typeof fitFrame>

/** The grey area under the toolbar. Measures itself and hands the fit down. */
function Stage({ device, children }: { device: Device; children: (fit: Fit) => ReactNode }) {
  const ref = useRef<HTMLDivElement>(null)
  // Before the first measurement — and always in a test, where nothing has a
  // size — the frame is drawn unscaled at a desktop-sized stage.
  const [size, setSize] = useState({ w: 1440, h: 900 })

  useLayoutEffect(() => {
    const el = ref.current
    if (!el) return
    const measure = () => {
      const rect = el.getBoundingClientRect()
      if (rect.width > 0 && rect.height > 0) setSize({ w: rect.width, h: rect.height })
    }
    measure()
    if (typeof ResizeObserver === 'undefined') return
    const observer = new ResizeObserver(measure)
    observer.observe(el)
    return () => observer.disconnect()
  }, [])

  const fit = fitFrame(device, size.w, size.h)
  return (
    <main ref={ref} className="relative min-h-0 flex-1 overflow-hidden">
      <div className="absolute inset-0 flex items-center justify-center">
        {/* The box the scaled frame occupies, so centring sees its real size. */}
        <div
          style={{ width: fit.outerW * fit.scale, height: fit.outerH * fit.scale }}
          className="relative"
        >
          <div
            className="absolute top-0 left-0 origin-top-left"
            style={{ width: fit.outerW, height: fit.outerH, transform: `scale(${fit.scale})` }}
          >
            {children(fit)}
          </div>
        </div>
      </div>
    </main>
  )
}

/** The outline around the frame: a window for desktop, a slab for tablet, a
 *  phone for mobile. The iframe stays in the same place in the tree for all
 *  three, so switching device reflows the page rather than reloading it. */
function DeviceFrame({ device, fit, children }: { device: Device; fit: Fit; children: ReactNode }) {
  const phone = device === 'mobile'
  return (
    <div
      data-frame={device}
      className={[
        'flex h-full w-full flex-col overflow-hidden',
        phone
          ? 'rounded-[2.75rem] bg-foreground shadow-2xl'
          : 'rounded-xl border border-border bg-background shadow-xl',
      ].join(' ')}
      style={phone ? { padding: BEZEL } : undefined}
    >
      {device === 'desktop' && (
        <div
          aria-hidden
          className="flex shrink-0 items-center gap-1.5 border-b border-border bg-muted px-3"
          style={{ height: WINDOW_BAR }}
        >
          <span className="h-3 w-3 rounded-full bg-border" />
          <span className="h-3 w-3 rounded-full bg-border" />
          <span className="h-3 w-3 rounded-full bg-border" />
        </div>
      )}
      <div
        className={phone ? 'overflow-hidden rounded-[2rem]' : 'min-h-0 flex-1'}
        style={phone ? { height: fit.viewportH } : undefined}
      >
        {children}
      </div>
    </div>
  )
}
